import { Alert } from "@mui/material";
import { SnackbarMessageProps } from "@knowfully/ui.common";
import ConfirmDialog from "components/ConfirmDialog";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useRef, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  AgreementResponse,
  HasPendingOrAcceptedAgreementRequest,
  ResourceV2ListItem,
  SaveResponse,
  SkuUserAgreementSaveRequestExtended,
  SkuUserAgreementValue,
} from "services/KnowfullyAdminClient";
import { Severity } from "types/common-types";
import { dateToStandardFormat } from "utils/dateUtils";
import translateError from "utils/translateError";
import {
  UserAgreementForm,
  UserAgreementFormValues,
} from "./UserAgreementForm";

export function UserAgreementCreateForm() {
  const {
    skuUserAgreementsUrl,
    skuUserAgreementDetailsUrl,
    skuUserAgreementService,
    resourceService,
  } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [showOrgMismatchDialog, setShowOrgMismatchDialog] = useState(false);
  const pendingSubmitValues = useRef<UserAgreementFormValues | null>(null);
  const selectedAgreement = useRef<AgreementResponse | null>(null);
  const selectedFaculty = useRef<ResourceV2ListItem | null>(null);

  useTitle(t({ id: "assignFacultyAgreement" }));

  const saveAgreement = async (values: UserAgreementFormValues) => {
    try {
      const response = await resourceService.hasPendingOrAcceptedAgreement(
        new HasPendingOrAcceptedAgreementRequest({
          agreementId: values.agreementId!,
          organizationUserId: values.organizationUserId!,
          skuId: values.skuId!,
        })
      );

      if (response.data === true) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "facultyAlreadyAssignedToAgreement" }),
            Severity.Error
          )
        );
        return;
      }

      const createdskuUserAgreement: SaveResponse = await registerPromise(
        skuUserAgreementService.saveSkuUserAgreement(
          new SkuUserAgreementSaveRequestExtended({
            skuUserAgreementValues: values?.additionalFields?.map(
              (x) =>
                new SkuUserAgreementValue({
                  name: x.key,
                  value:
                    x.key_type === "date" &&
                    x.value instanceof Date &&
                    !isNaN(x.value.getTime())
                      ? dateToStandardFormat(x.value)
                      : x.value.toString(),
                  type: x.key_type,
                })
            ),
            userId: values.organizationUserId,
            organizationId: values.organizationId,
            ...values,
          })
        )
      );

      queryClient.invalidateQueries("getSkuUserAgreements");
      queryClient.invalidateQueries("skuUserAgreementService.getSkuUserAgreementAgreements");
      navigate(skuUserAgreementDetailsUrl(createdskuUserAgreement.id));
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onSubmit = async (values: UserAgreementFormValues) => {
    const facultyOrgId = selectedFaculty.current?.organizationId;
    const agreementOrgId = selectedAgreement.current?.organizationId;

    if (facultyOrgId && agreementOrgId && facultyOrgId !== agreementOrgId) {
      pendingSubmitValues.current = values;
      setShowOrgMismatchDialog(true);
      return;
    }

    return saveAgreement(values);
  };

  const onConfirmOrgMismatch = async () => {
    setShowOrgMismatchDialog(false);
    if (pendingSubmitValues.current) {
      await saveAgreement(pendingSubmitValues.current);
      pendingSubmitValues.current = null;
    }
  };

  const onAgreementSelected = useCallback((agreement: AgreementResponse | null) => {
    selectedAgreement.current = agreement;
  }, []);

  const onFacultySelected = useCallback((faculty: ResourceV2ListItem | null) => {
    selectedFaculty.current = faculty;
  }, []);

  return (
    <>
      <RedirectBackButton
        redirectRoute={skuUserAgreementsUrl()}
        labelKey="back"
      />

      <h1>
        <T id="assignFacultyAgreement" />
      </h1>
      <UserAgreementForm
        onSubmit={onSubmit}
        onAgreementSelected={onAgreementSelected}
        onFacultySelected={onFacultySelected}
      />
      <ConfirmDialog
        open={showOrgMismatchDialog}
        dialogTitleKey="organizationMismatchTitle"
        rawContent={
          <Alert severity="warning">
            <T id="organizationMismatchMessage" />
          </Alert>
        }
        onCancel={() => setShowOrgMismatchDialog(false)}
        handleConfirm={onConfirmOrgMismatch}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
}
